import { useContext } from "react";

import { AppConfigContext } from "../../../../core/configuration/Configuration";
import { useAuthorizedFetch } from "../../../../core/data-access/useAuthorizedFetch";
import { cacheRequests } from "../../../utils/cacheRequests";
import { AspectRatiosResponse } from "./AspectRatiosResponse";
import { AspectTypesResponse } from "./AspectTypesResponse";
import { CourseOfActionsResponse } from "./CourseOfActionsResponse";
import { FixedBillingsResponse } from "./FixedBillingsResponse";
import { RejectionReasonsResponse } from "./RejectionReasonsResponse";
import { StandardsResponse } from "./StandardsResponse";

const standardsCache = cacheRequests<StandardsResponse>();
const aspectRatiosCache = cacheRequests<AspectRatiosResponse>();
const aspectTypesCache = cacheRequests<AspectTypesResponse>();
const rejectionReasonsCache = cacheRequests<RejectionReasonsResponse>();
const courseOfActionsCache = cacheRequests<CourseOfActionsResponse>();
const fixedBillingsCache = cacheRequests<CourseOfActionsResponse>();

export const useMasterLookupsApi = () => {
    const appConfigContext = useContext(AppConfigContext);
    const { httpGet } = useAuthorizedFetch();
    const baseUrl = appConfigContext.masterConfig.apiUrl;

    const getAspectTypes = async (): Promise<AspectTypesResponse> =>
        aspectTypesCache.getRequest({
            requestFn: () => httpGet<AspectRatiosResponse>(`${baseUrl}/aspect-types`),
            key: "aspect-types",
            ttlMs: 20000
        });

    const getAspectRatios = async (): Promise<AspectRatiosResponse> =>
        aspectRatiosCache.getRequest({
            requestFn: () => httpGet<AspectRatiosResponse>(`${baseUrl}/aspect-ratios`),
            key: "aspect-ratios",
            ttlMs: 20000
        });

    const getStandards = async (): Promise<StandardsResponse> =>
        standardsCache.getRequest({
            requestFn: () => httpGet<StandardsResponse>(`${baseUrl}/standards`),
            key: "standards",
            ttlMs: 20000
        });

    const getRejectionReasons = async (): Promise<RejectionReasonsResponse> => {
        return rejectionReasonsCache.getRequest({
            requestFn: () => httpGet<RejectionReasonsResponse>(`${baseUrl}/rejection-reasons`),
            key: "rejection-reasons"
        });
    };

    const getCourseOfActions = async (): Promise<CourseOfActionsResponse> =>
        courseOfActionsCache.getRequest({
            requestFn: () => httpGet<CourseOfActionsResponse>(`${baseUrl}/file-records/get-course-of-actions`),
            key: "course-of-actions"
        });

    const getFixedBillings = async (): Promise<FixedBillingsResponse> =>
        fixedBillingsCache.getRequest({
            requestFn: () => httpGet<FixedBillingsResponse>(`${baseUrl}/file-records/get-fixed-billings`),
            key: "fixed-billings"
        });

    return {
        getAspectTypes,
        getAspectRatios,
        getStandards,
        getRejectionReasons,
        getCourseOfActions,
        getFixedBillings
    };
};
